/**
 * Invoice Service - API calls for invoice management
 */

import {CancelablePromise} from './openapi';
import {OpenAPI} from './openapi/core/OpenAPI';
import {request as __request} from './openapi/core/request';

// ==================== Types ====================

export interface InvoiceEmitter {
    id: number;
    short_key: string;
    name: string;
    trading_name?: string;
    street: string;
    additional_street?: string;
    postal_code: string;
    city: string;
    country_code: string;
    vat_id?: string;
    tax_number?: string;
    company_id?: string;
    iban: string;
    bic?: string;
    bank_name?: string;
    account_holder?: string;
    contact_name?: string;
    contact_phone?: string;
    contact_email?: string;
    // Additional company info for PDF generation
    website?: string;
    fax?: string;
    management?: string; // Geschäftsführung (pipe-separated)
    court_registration?: string; // Amtsgericht/HRB
    po_box?: string; // Postfach
    logo_path?: string;
    active: boolean;
    created_at?: string;
    updated_at?: string;
}

export interface InvoiceEmitterCreate {
    short_key: string;
    name: string;
    trading_name?: string;
    street: string;
    additional_street?: string;
    postal_code: string;
    city: string;
    country_code?: string;
    vat_id?: string;
    tax_number?: string;
    company_id?: string;
    iban: string;
    bic?: string;
    bank_name?: string;
    account_holder?: string;
    contact_name?: string;
    contact_phone?: string;
    contact_email?: string;
    // Additional company info for PDF generation
    website?: string;
    fax?: string;
    management?: string;
    court_registration?: string;
    po_box?: string;
    logo_path?: string;
    active?: boolean;
}

export interface InvoiceEmitterUpdate {
    short_key?: string;
    name?: string;
    trading_name?: string;
    street?: string;
    additional_street?: string;
    postal_code?: string;
    city?: string;
    country_code?: string;
    vat_id?: string;
    tax_number?: string;
    company_id?: string;
    iban?: string;
    bic?: string;
    bank_name?: string;
    account_holder?: string;
    contact_name?: string;
    contact_phone?: string;
    contact_email?: string;
    // Additional company info for PDF generation
    website?: string;
    fax?: string;
    management?: string;
    court_registration?: string;
    po_box?: string;
    logo_path?: string;
    active?: boolean;
}

export interface InvoiceProduct {
    id: number;
    emitter_id?: number;
    name: string;
    description?: string;
    seller_item_id?: string;
    default_price?: number;
    unit_code: string;
    unit_label?: string;
    vat_category_code: string;
    vat_rate: number;
    active: boolean;
    created_at?: string;
    updated_at?: string;
}

export interface InvoiceProductCreate {
    emitter_id?: number;
    name: string;
    description?: string;
    seller_item_id?: string;
    default_price?: number;
    unit_code?: string;
    unit_label?: string;
    vat_category_code?: string;
    vat_rate?: number;
    active?: boolean;
}

export interface InvoiceProductUpdate {
    emitter_id?: number;
    name?: string;
    description?: string;
    seller_item_id?: string;
    default_price?: number;
    unit_code?: string;
    unit_label?: string;
    vat_category_code?: string;
    vat_rate?: number;
    active?: boolean;
}

export interface InvoiceLineItemCreate {
    line_number: number;
    description: string;
    quantity: number;
    unit_code: string;
    unit_price: number;
    vat_category_code?: string;
    vat_rate?: number;
    product_id?: number;
    note?: string;
    seller_item_id?: string;
}

export interface InvoiceLineItemUpdate {
    line_number?: number;
    description?: string;
    quantity?: number;
    unit_code?: string;
    unit_price?: number;
    vat_category_code?: string;
    vat_rate?: number;
    product_id?: number;
    note?: string;
    seller_item_id?: string;
}

export interface InvoiceCreate {
    emitter_id: number;
    invoice_number?: number;  // Manual invoice number (required before finalization)
    buyer_name?: string;
    buyer_street?: string;
    buyer_additional_street?: string;
    buyer_postal_code?: string;
    buyer_city?: string;
    buyer_country_code?: string;
    buyer_vat_id?: string;
    buyer_reference?: string;
    buyer_contact_name?: string;
    buyer_contact_phone?: string;
    buyer_contact_email?: string;
    buyer_full_contact?: string;
    objekt?: string;
    work_description?: string;
    work_date?: string;
    note?: string;
    delivery_date?: string;
    due_date?: string;
    payment_reference?: string;
    // Skonto (Cash Discount)
    skonto_percentage?: number;
    skonto_days?: number;
    // Payment Terms
    payment_days?: number;
    line_items?: InvoiceLineItemCreate[];
    task_id?: number;
}

export interface InvoiceUpdate {
    invoice_number?: number;  // Manual invoice number (required before finalization)
    buyer_name?: string;
    buyer_street?: string;
    buyer_additional_street?: string;
    buyer_postal_code?: string;
    buyer_city?: string;
    buyer_country_code?: string;
    buyer_vat_id?: string;
    buyer_reference?: string;
    buyer_contact_name?: string;
    buyer_contact_phone?: string;
    buyer_contact_email?: string;
    buyer_full_contact?: string;
    objekt?: string;
    work_description?: string;
    work_date?: string;
    note?: string;
    delivery_date?: string;
    due_date?: string;
    payment_reference?: string;
    // Skonto (Cash Discount)
    skonto_percentage?: number;
    skonto_days?: number;
    // Payment Terms
    payment_days?: number;
}

export interface Invoice {
    id: number;
    emitter_id: number;
    invoice_number?: number;
    status: 'DRAFT' | 'FINALIZED' | 'SENT' | 'PAID' | 'CANCELLED';
    invoice_date?: string;
    delivery_date?: string;
    due_date?: string;
    invoice_type_code: string;
    currency_code: string;
    buyer_name?: string;
    buyer_street?: string;
    buyer_additional_street?: string;
    buyer_postal_code?: string;
    buyer_city?: string;
    buyer_country_code?: string;
    buyer_vat_id?: string;
    buyer_reference?: string;
    buyer_contact_name?: string;
    buyer_contact_phone?: string;
    buyer_contact_email?: string;
    buyer_full_contact?: string;
    task_id?: number;
    objekt?: string;
    work_description?: string;
    work_date?: string;
    note?: string;
    payment_reference?: string;
    // Skonto (Cash Discount)
    skonto_percentage?: number;
    skonto_days?: number;
    skonto_amount?: number;
    // Payment Terms
    payment_days?: number;
    // PDF Document
    pdf_path?: string;
    line_extension_amount?: number;
    tax_exclusive_amount?: number;
    tax_inclusive_amount?: number;
    payable_amount?: number;
    created_at?: string;
    updated_at?: string;
    created_by?: string;
    updated_by?: string;
    archived_at?: string;
    // Stornorechnung (Cancellation Invoice) fields
    is_storno?: boolean;
    storno_of_invoice_id?: number;  // For Stornorechnungen: ID of the original invoice
    storno_of_invoice_number?: string;  // For Stornorechnungen: formatted invoice number of original
    storno_invoice_id?: number;  // For original invoices: ID of the Stornorechnung
}

export interface InvoiceLineItem {
    id: number;
    invoice_id: number;
    line_number: number;
    product_id?: number;
    description: string;
    note?: string;
    seller_item_id?: string;
    quantity: number;
    unit_code: string;
    unit_price: number;
    line_net_amount: number;
    vat_category_code: string;
    vat_rate: number;
    created_at?: string;
    updated_at?: string;
}

export interface InvoiceVatBreakdown {
    id: number;
    invoice_id: number;
    vat_category_code: string;
    vat_rate: number;
    taxable_amount: number;
    tax_amount: number;
    created_at?: string;
}

export interface InvoiceDetail extends Invoice {
    emitter?: InvoiceEmitter;
    line_items: InvoiceLineItem[];
    vat_breakdown: InvoiceVatBreakdown[];
}

export interface InvoiceListResponse {
    items: Invoice[];
    total: number;
    skip: number;
    limit: number;
}

// ==================== Utility Functions ====================

/**
 * Format invoice number as "KD YEAR / NR SHORT_KEY"
 * @param invoiceNumber - The sequential invoice number
 * @param invoiceDate - The invoice date (to extract year)
 * @param emitterShortKey - The emitter's short key
 * @returns Formatted invoice number string, e.g., "KD 2026 / 1 BOE"
 */
export function formatInvoiceNumber(
    invoiceNumber: number | undefined | null,
    invoiceDate: string | undefined | null,
    emitterShortKey: string | undefined | null
): string {
    if (!invoiceNumber) return 'Entwurf';

    const year = invoiceDate ? new Date(invoiceDate).getFullYear() : new Date().getFullYear();
    const shortKey = emitterShortKey || '';

    return `${year}/${invoiceNumber}`.trim();
}

// ==================== Service ====================

export class InvoiceService {
    // ==================== Emitters ====================

    /**
     * List all invoice emitters
     */
    public static getEmitters(activeOnly: boolean = true): CancelablePromise<InvoiceEmitter[]> {
        return __request(OpenAPI, {
            method: 'GET',
            url: '/invoices/emitters',
            query: {
                active_only: activeOnly,
            },
        });
    }

    /**
     * Get single emitter by ID
     */
    public static getEmitter(emitterId: number): CancelablePromise<InvoiceEmitter> {
        return __request(OpenAPI, {
            method: 'GET',
            url: `/invoices/emitters/${emitterId}`,
        });
    }

    /**
     * Create new emitter
     */
    public static createEmitter(data: InvoiceEmitterCreate): CancelablePromise<InvoiceEmitter> {
        return __request(OpenAPI, {
            method: 'POST',
            url: '/invoices/emitters',
            body: data,
            mediaType: 'application/json',
            errors: {
                422: 'Validation Error',
            },
        });
    }

    /**
     * Update emitter
     */
    public static updateEmitter(emitterId: number, data: InvoiceEmitterUpdate): CancelablePromise<InvoiceEmitter> {
        return __request(OpenAPI, {
            method: 'PUT',
            url: `/invoices/emitters/${emitterId}`,
            body: data,
            mediaType: 'application/json',
            errors: {
                422: 'Validation Error',
            },
        });
    }

    /**
     * Delete (deactivate) emitter
     */
    public static deleteEmitter(emitterId: number): CancelablePromise<any> {
        return __request(OpenAPI, {
            method: 'DELETE',
            url: `/invoices/emitters/${emitterId}`,
        });
    }

    // ==================== Products ====================

    /**
     * List all invoice products
     */
    public static getProducts(activeOnly: boolean = true, emitterId?: number): CancelablePromise<InvoiceProduct[]> {
        return __request(OpenAPI, {
            method: 'GET',
            url: '/invoices/products',
            query: {
                active_only: activeOnly,
                emitter_id: emitterId,
            },
        });
    }

    /**
     * Get single product by ID
     */
    public static getProduct(productId: number): CancelablePromise<InvoiceProduct> {
        return __request(OpenAPI, {
            method: 'GET',
            url: `/invoices/products/${productId}`,
        });
    }

    /**
     * Create new product
     */
    public static createProduct(data: InvoiceProductCreate): CancelablePromise<InvoiceProduct> {
        return __request(OpenAPI, {
            method: 'POST',
            url: '/invoices/products',
            body: data,
            mediaType: 'application/json',
            errors: {
                422: 'Validation Error',
            },
        });
    }

    /**
     * Update product
     */
    public static updateProduct(productId: number, data: InvoiceProductUpdate): CancelablePromise<InvoiceProduct> {
        return __request(OpenAPI, {
            method: 'PUT',
            url: `/invoices/products/${productId}`,
            body: data,
            mediaType: 'application/json',
            errors: {
                422: 'Validation Error',
            },
        });
    }

    /**
     * Delete (deactivate) product
     */
    public static deleteProduct(productId: number): CancelablePromise<any> {
        return __request(OpenAPI, {
            method: 'DELETE',
            url: `/invoices/products/${productId}`,
        });
    }

    // ==================== Invoices ====================

    /**
     * List invoices with pagination and filtering.
     * Returns paginated results with total count.
     */
    public static getInvoices(
        options: {
            skip?: number;
            limit?: number;
            status?: string;
            emitterId?: number;
            includeArchived?: boolean;
            dateFrom?: string;
            dateTo?: string;
        } = {}
    ): CancelablePromise<InvoiceListResponse> {
        return __request(OpenAPI, {
            method: 'GET',
            url: '/invoices/',
            query: {
                skip: options.skip,
                limit: options.limit,
                status: options.status,
                emitter_id: options.emitterId,
                include_archived: options.includeArchived ?? false,
                date_from: options.dateFrom,
                date_to: options.dateTo,
            },
        });
    }

    /**
     * Get single invoice with details
     */
    public static getInvoice(invoiceId: number): CancelablePromise<InvoiceDetail> {
        return __request(OpenAPI, {
            method: 'GET',
            url: `/invoices/${invoiceId}`,
        });
    }

    /**
     * Finalize invoice (assign number)
     */
    public static finalizeInvoice(invoiceId: number): CancelablePromise<any> {
        return __request(OpenAPI, {
            method: 'POST',
            url: `/invoices/${invoiceId}/finalize`,
        });
    }

    /**
     * Upload invoice PDF file
     */
    public static uploadInvoicePdf(invoiceId: number, file: File): CancelablePromise<Invoice> {
        const formData = new FormData();
        formData.append('file', file);
        return __request(OpenAPI, {
            method: 'POST',
            url: `/invoices/${invoiceId}/upload-pdf`,
            body: formData,
        });
    }

    /**
     * Update invoice status
     */
    public static updateInvoiceStatus(invoiceId: number, status: string): CancelablePromise<Invoice> {
        return __request(OpenAPI, {
            method: 'POST',
            url: `/invoices/${invoiceId}/status`,
            body: {status},
            mediaType: 'application/json',
        });
    }

    /**
     * Create new invoice (as DRAFT)
     */
    public static createInvoice(data: InvoiceCreate): CancelablePromise<Invoice> {
        return __request(OpenAPI, {
            method: 'POST',
            url: '/invoices/',
            body: data,
            mediaType: 'application/json',
            errors: {
                422: 'Validation Error',
            },
        });
    }

    /**
     * Update invoice (only DRAFT invoices)
     */
    public static updateInvoice(invoiceId: number, data: InvoiceUpdate): CancelablePromise<Invoice> {
        return __request(OpenAPI, {
            method: 'PUT',
            url: `/invoices/${invoiceId}`,
            body: data,
            mediaType: 'application/json',
            errors: {
                422: 'Validation Error',
            },
        });
    }

    /**
     * Add line item to invoice
     */
    public static addLineItem(invoiceId: number, data: InvoiceLineItemCreate): CancelablePromise<InvoiceLineItem> {
        return __request(OpenAPI, {
            method: 'POST',
            url: `/invoices/${invoiceId}/line-items`,
            body: data,
            mediaType: 'application/json',
            errors: {
                422: 'Validation Error',
            },
        });
    }

    /**
     * Update line item
     */
    public static updateLineItem(invoiceId: number, itemId: number, data: InvoiceLineItemUpdate): CancelablePromise<InvoiceLineItem> {
        return __request(OpenAPI, {
            method: 'PUT',
            url: `/invoices/${invoiceId}/line-items/${itemId}`,
            body: data,
            mediaType: 'application/json',
            errors: {
                422: 'Validation Error',
            },
        });
    }

    /**
     * Delete line item
     */
    public static deleteLineItem(invoiceId: number, itemId: number): CancelablePromise<void> {
        return __request(OpenAPI, {
            method: 'DELETE',
            url: `/invoices/${invoiceId}/line-items/${itemId}`,
        });
    }

    /**
     * Archive (soft delete) invoice
     */
    public static archiveInvoice(invoiceId: number, reason?: string): CancelablePromise<any> {
        return __request(OpenAPI, {
            method: 'DELETE',
            url: `/invoices/${invoiceId}`,
            body: reason ? {reason} : undefined,
            mediaType: 'application/json',
        });
    }

    /**
     * Create Stornorechnung (cancellation invoice)
     * Creates a copy of the invoice with negative amounts and cancels the original
     * @param invoiceId - The ID of the invoice to cancel
     * @param invoiceNumber - The invoice number to assign to the Stornorechnung (required)
     */
    public static createStornoInvoice(invoiceId: number, invoiceNumber: number): CancelablePromise<Invoice> {
        return __request(OpenAPI, {
            method: 'POST',
            url: `/invoices/${invoiceId}/storno`,
            query: {
                invoice_number: invoiceNumber,
            },
        });
    }

    /**
     * Permanently delete invoice (Administrator only)
     * Deletes the invoice, all related data, and the PDF file.
     * WARNING: This action cannot be undone!
     * @param invoiceId - The ID of the invoice to delete
     */
    public static permanentDeleteInvoice(invoiceId: number): CancelablePromise<any> {
        return __request(OpenAPI, {
            method: 'DELETE',
            url: `/invoices/${invoiceId}/permanent`,
        });
    }
}

export default InvoiceService;
